import { connect } from "react-redux";
import React from "react";
import ReactQuill, {Quill} from "react-quill";
import moment from 'moment';
import { ElementAttributeType, DynamoNode, ElementNode, InputNode, ElementType, NodeTag } from "dynamo-models";
import { IEditorState, doSetAttribute } from "../../redux";
import { Checkbox, TextField, FormControl, InputLabel, Select, MenuItem, RadioGroup, FormControlLabel, Radio, IconButton } from "@material-ui/core";
import { EditorImageSelectionWidget } from "./editorImageSelectionWidget";

type ApplyTuple = (get: () => any, set: (value: any) => void, value: any) => void;

interface IAttributesProp {
    allowedProperties: ElementAttributeType[];
    node?: DynamoNode;
    maxHeight?: number;

    bubble: ApplyTuple;
}

const commonStyle = ({
    marginTop: 5,
    marginBottom: 5,
    marginLeft: 10,
    marginRight: 10
});

const getDefaultValueRender = (node: InputNode<any>, apply: ApplyTuple) => {
    const text = "Default Value";

    const getOnInput = v => event => apply(() => v.defaultValue, w => v.defaultValue = w, v.ElementType === ElementType.Checkbox ? event.target.checked : event.target.value);

    const getProps = v => ({
        value: v.defaultValue,
        onInput: getOnInput(v),
        text: text,
        label: text
    });

    const nodeProps = getProps(node);

    switch (node.elementType) {
        case ElementType.Checkbox:
        return (
            <FormControlLabel
                style={{...commonStyle, width: "100%"}}
                {...nodeProps}
                control={
                    <Checkbox defaultChecked={nodeProps.value} onInput={nodeProps.onInput} />
                }
            />
        )

        case ElementType.Date:
        return (
            <TextField
                type="date"
                style={commonStyle}
                InputLabelProps={{
                    shrink: true,
                }}
                {...nodeProps} />
        );

        case ElementType.DropdownGroup:
        return (
            <FormControl style={commonStyle}>
                <InputLabel>{nodeProps.text}</InputLabel>
                <Select
                    fullWidth
                    {...nodeProps}
                    style={{width: "100%"}}>
                        <MenuItem value="None" key={-1}>
                            None
                        </MenuItem>
                        {node.children.map(v => (
                            <MenuItem {...{onSelect: nodeProps.onInput, key: v.nodeId}}>
                                {v.text}
                            </MenuItem>
                        ))}
                </Select>
            </FormControl>
        );

        case ElementType.RadioButtonGroup:
        return (
            <RadioGroup radioGroup="defaultValue" value={node.defaultValue}>
            {
                node.children.map(v => (props => (
                    <FormControlLabel
                        label={props.text}
                        style={commonStyle}
                        control={
                            <Radio radioGroup="defaultvalue" onChange={props.onInput} checked={props.value} />
                        }
                    />
                ))(getProps(v)))
            }
            </RadioGroup>
        );

        case ElementType.Textbox:
        return (
            <TextField
                {...nodeProps} 
                key={node.nodeId}
                multiline={node.isMultiline}
                style={commonStyle}
                rows={node.isMultiline ? "6" : "0"}
            />
        )
    }
}

const getAttributeRender =  (node: DynamoNode, bubble: ApplyTuple) => (type: ElementAttributeType) => {
    const getTextBoxRender = (get, set, label, isDisabled = false, type = "text", rows = 1) => (onInput => (
        <TextField
            key={label}
            type={type}
            label={label}
            inputProps={{
                disabled: isDisabled,
                style: commonStyle
            }}
            style={commonStyle}
            onChange={onInput}
            multiline
            rows={rows}
            value={get() || ""}
        />
    ))(event => bubble(get, set, event.target.value));

    const getSelectRender = (get, set, label, options, isDisabled = false) => ((onInput) => (
        <FormControl>
            <InputLabel>{label}</InputLabel>
            <Select
                label={label}
                key={label}
                onChange={onInput}
                value={get() ? get() : ""}
                disabled={isDisabled}
            >
                {
                    options.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                            {option.display}
                        </MenuItem>
                    ))
                }
            </Select>
        </FormControl>
    ))((event) => bubble(get, set, event.target.value));

    const getRichTextEditor = (get, set, label, isDisabled = false) => ((onInput, format, modules) => (
        <div style={{ marginTop: "1em" }}>
            <ReactQuill
                readOnly={isDisabled}
                value={get() || ""}
                onChange={(text, delta, source, editor) => {
                    if (source === "user") {
                        onInput(text);
                    }
                }}
                theme="snow"
                formats={format}
                modules={modules}
            />
        </div>
    ))(
        value => bubble(get, set, value), [
            'background', 
            'bold',
            'color',
            'font',
            'italic',
            'link',
            'size',
            'underline',
            'blockquote',
            'header',
            'indent',
            'list',
            'align',
            'direction',
            'image',
            'video',
        ], {
            toolbar: [
                ['bold', 'italic', 'underline', 'strike'],
                [{ 'header': 1 }, { 'header': 2 }],
                [{ 'list': 'ordered'}, { 'list': 'bullet' }],
                [{ 'indent': '-1'}, { 'indent': '+1' }],
                [{ 'direction': 'rtl' }],
                ['link', 'video'],

                [{ 'size': ['small', false, 'large', 'huge'] }], 
                [{ 'header': [1, 2, 3, 4, 5, 6, false] }],

                [{ 'color': [] }, { 'background': [] }],
                [{ 'font': [] }],
                [{ 'align': [] }],

                ['clean'] 
            ]
        },
    );

    const getCheckBoxRender = (get, set, label) => (onInput => (
        <FormControlLabel
            key={label}
            label={label}
            style={commonStyle}
            control={
                <Checkbox checked={get() || false} onChange={onInput} />
            }
        />
    ))(event => bubble(get, set, event.target.checked));

    const trustedNode = node as any;

    switch (type) {
        case ElementAttributeType.DefaultValue:
        return getDefaultValueRender(trustedNode, bubble);

        case ElementAttributeType.FutureDateMessage:
        return getTextBoxRender(() => trustedNode.futureDateMessage, v => trustedNode.futureDateMessage = v, "Future Date Message");

        case ElementAttributeType.InvalidDateMessage:
        return getTextBoxRender(() => trustedNode.invalidDateMessage, v => trustedNode.invalidDateMessage = v, "Invalid Date Message");

        case ElementAttributeType.InvalidZipCodeMessage:
        return getTextBoxRender(() => trustedNode.invalidZipCodeMessage, v => trustedNode.invalidZipCodeMessage = v, "Invalid ZIP Code Message");

        case ElementAttributeType.IsDisabled:
        return getCheckBoxRender(() => trustedNode.isDisabled, v => trustedNode.isDisabled = v, "Disabled?");

        case ElementAttributeType.IsHidden:
        return getCheckBoxRender(() => trustedNode.isHidden, v => trustedNode.isHidden = v, "Hidden?");

        case ElementAttributeType.IsMultiline:
        return getCheckBoxRender(() => trustedNode.isMultiline, v => trustedNode.isMultiline = v, "Multiline?");

        case ElementAttributeType.MaxLength:
        return getTextBoxRender(() => trustedNode.maxLength, v => trustedNode.maxLength = v, "Max Length");

        case ElementAttributeType.PastDateMessage:
        return getTextBoxRender(() => trustedNode.pastDateMessage, v => trustedNode.pastDateMessage = v, "Invalid Past Date Message");

        case ElementAttributeType.Placeholder:
        return getTextBoxRender(() => trustedNode.placeholder, v => trustedNode.placeholder = v, "Placeholder");

        case ElementAttributeType.RequiredMessage:
        return getTextBoxRender(() => trustedNode.requiredMessage, v => trustedNode.requiredMessage = v, "Required Message");

        case ElementAttributeType.Text:
        return getRichTextEditor(() => trustedNode.text, v => trustedNode.text = v, "Text");

        case ElementAttributeType.URL:
        if (trustedNode.elementType === ElementType.Image) {
            return <EditorImageSelectionWidget onUrlChanged={(event) => bubble(() => trustedNode.url, v => trustedNode.url = v, event.target.value)} />;
        }
        return getTextBoxRender(() => trustedNode.url, v => trustedNode.url = v, "Url");

        case ElementAttributeType.DateFormat:
        return getSelectRender(() => trustedNode.dateFormat, v => trustedNode.dateFormat = v, "Date Format", [
            {
                display: moment().format("dddd, MMM D, YYYY [|] h:mm a"),
                value: "dddd, MMMM D, YYYY [|] h:mm a",
            },
            {
                display: moment().format("ddd, MMM D h:mm a"),
                value: "ddd, MMM D h:mm a",
            },
        ]);

        default:
        return null;
    }
}

class AttributesWidget extends React.Component<IAttributesProp & any> {
    public render() {
        const {
            allowedProperties,
            node,
            bubble,
            metaNode,
            maxHeight,
        } = this.props;

        if (node === undefined || metaNode === undefined) {
            return null;
        }

        const render = getAttributeRender(node, bubble);

        return (
            <div style={{display: "flex", flexDirection: "column", maxHeight: maxHeight !== undefined ? `${maxHeight}em` : undefined, overflowY: "auto" }}>
                <TextField
                    key="inputName"
                    label="Name"
                    inputProps={{
                        style: commonStyle
                    }}
                    style={commonStyle}
                    onChange={e => bubble(() => node.dynamicName, v => node.dynamicName = v, e.currentTarget.value)}
                    value={node.dynamicName}
                />
            {
                allowedProperties.map(render)
            }
            </div>
        );
    }
}

const getAllowedProperties = (nodeMaybe: DynamoNode | undefined) => {
    if (nodeMaybe === undefined || nodeMaybe.tag !== NodeTag.Element) {
        return [];
    }

    const elementNode = nodeMaybe as ElementNode;
    const baseAttributes = [ElementAttributeType.IsHidden];
    const baseTextAttributes = [...baseAttributes, ElementAttributeType.Text]
    const baseInputAttributes = [ElementAttributeType.IsDisabled, ...baseTextAttributes, ElementAttributeType.DefaultValue];
    const baseTextBoxAttributes = [...baseInputAttributes, ElementAttributeType.Placeholder, ElementAttributeType.RequiredMessage];

    switch (elementNode.elementType) {
        case ElementType.Cell:
        return baseAttributes;

        case ElementType.Checkbox:
        return baseInputAttributes;

        case ElementType.Date:
        return [...baseTextBoxAttributes, ElementAttributeType.FutureDateMessage, ElementAttributeType.InvalidDateMessage, ElementAttributeType.PastDateMessage];

        case ElementType.DropdownElement:
        return baseTextAttributes;

        case ElementType.DropdownGroup:
        return baseInputAttributes;

        case ElementType.RadioButtonElement:
        return baseInputAttributes;

        case ElementType.RadioButtonGroup:
        return baseTextAttributes;

        case ElementType.Row:
        return baseAttributes;

        case ElementType.Section:
        return baseAttributes;

        case ElementType.Table:
        return baseAttributes;

        case ElementType.Textbox:
        return baseInputAttributes;

        case ElementType.VariableText:
        return [...baseTextAttributes, ElementAttributeType.DateFormat];

        case ElementType.Image:
        return [...baseAttributes, ElementAttributeType.URL];

        default:
        return [];
    }
}

const stateToProps = (state: IEditorState) => ({
    node: state.selectedNode,
    allowedProperties: getAllowedProperties(state.selectedNode),
    metaNode: state.container
})

const dispatchToProps = dispatch => ({
    bubble: (get, set, value) => (curr => dispatch(doSetAttribute(() => set(value), () => set(curr))))(get())
})

export const EditorPropertiesWidget = connect(stateToProps, dispatchToProps)(AttributesWidget);